/**
 * Camera Feeds' linking rules (`camera-feeds-page-link`), kept pure so each one is tested without
 * rendering. The stage decides WHEN a camera's stream changes; these decide what its name becomes
 * and what the Replace confirm says.
 */

/**
 * A camera's name after it is linked to a stream (spec R5, epic ledger row 50).
 *
 * A camera that was never named, or that is still named after the stream it showed, follows the
 * new stream. A camera the operator named keeps its name. `oldStreamName` is `null` when the camera
 * showed no stream or its stream's name has not been read — a name is never compared against a
 * fallback such as "Stream 12", so an unknown old name leaves a named camera alone.
 */
export const nameAfterLink = ({
  name,
  oldStreamName,
  newStreamName,
}: {
  name: string;
  oldStreamName: string | null;
  newStreamName: string;
}): string => {
  const trimmed = name.trim();
  if (trimmed === '') return newStreamName;
  if (oldStreamName != null && trimmed === oldStreamName) return newStreamName;
  return name;
};

/**
 * A camera's name after its stream is unlinked (spec R6, ruling 3, maria@twinlabs.ai 2026-10-11).
 *
 * A name taken from the stream is cleared, so the camera reads "Camera N" again and the next stream
 * linked names it. A name the operator typed — or one that cannot be compared because the stream's
 * name is unknown — is kept.
 */
export const nameAfterUnlink = ({
  name,
  streamName,
}: {
  name: string;
  streamName: string | null;
}): string => (streamName != null && name.trim() === streamName ? '' : name);

/**
 * The Replace confirm's title and body (spec R7, frame 08). The second sentence says why the name
 * changes, and only when it does: `after-old` — the camera was named after the stream it showed
 * (frame 08's wording); `unnamed` — it had no name, so it takes the new stream's (review: saying
 * "named after" there would be false). The holder sentence is said only when the dropped stream
 * leaves another camera on this floor.
 */
export const replaceSentences = ({
  oldName,
  newName,
  renames,
  holderName,
}: {
  oldName: string;
  newName: string;
  renames: false | 'after-old' | 'unnamed';
  holderName: string | null;
}): { title: string; body: string } => {
  const kept = 'The camera keeps its position and cone';
  const sentences = [
    `This camera shows ${oldName}. ${newName} takes its place, and ${oldName} goes back to Not placed.`,
    renames === 'after-old'
      ? `${kept}, and is renamed ${newName} because it was named after ${oldName}.`
      : renames === 'unnamed'
        ? `${kept}, and takes the name ${newName}.`
        : `${kept}.`,
  ];
  if (holderName) sentences.push(`${holderName} no longer shows ${newName}.`);
  return { title: `Replace ${oldName} with ${newName}?`, body: sentences.join(' ') };
};

/**
 * The streams the picker offers (spec R2): every stream no camera on ANY floor holds, minus the
 * camera's own, in the wall's order. `heldIds` must span every floor — a stream held downstairs is
 * placed, and offering it here would only end in the cross-floor refusal.
 */
export const pickerStreams = <S extends { id: number }>({
  streams,
  heldIds,
  exclude,
  order,
}: {
  streams: readonly S[];
  heldIds: ReadonlySet<number>;
  exclude?: number | null;
  order?: (a: S, b: S) => number;
}): S[] => {
  const offered = streams.filter(s => !heldIds.has(s.id) && s.id !== exclude);
  return order ? offered.sort(order) : offered;
};
